/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { ExclamationCircleFilled } from '@ant-design/icons';
import {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from '@excalidraw/excalidraw/dist/types/excalidraw/types';
import { Alert, App, Button } from 'antd';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

const Excalidraw = dynamic(
  async () => {
    const mod = await import('@excalidraw/excalidraw');
    const { Excalidraw, MainMenu } = mod;
    return (props: any) => (
      <Excalidraw {...props}>
        <MainMenu>
          <MainMenu.DefaultItems.LoadScene />
          <MainMenu.DefaultItems.Export />
          <MainMenu.DefaultItems.SaveAsImage />
          <MainMenu.DefaultItems.SearchMenu />
          <MainMenu.DefaultItems.Help />
          <MainMenu.DefaultItems.ClearCanvas />
          <MainMenu.Separator />
          <MainMenu.DefaultItems.ToggleTheme />
          <MainMenu.DefaultItems.ChangeCanvasBackground />
        </MainMenu>
      </Excalidraw>
    );
  },
  { ssr: false }
);

export type ExcalidrawInitialElements = ExcalidrawInitialDataState['elements'];

type Props = {
  closeOnClickOutside?: boolean;
  /**
   * The initial set of elements to draw into the scene
   */
  initialElements: ExcalidrawInitialElements;
  /**
   * The initial set of elements to draw into the scene
   */
  initialAppState: Partial<AppState>;
  /**
   * The initial set of elements to draw into the scene
   */
  initialFiles: BinaryFiles;
  /**
   * Controls the visibility of the modal
   */
  // isShown?: boolean;
  /**
   * Callback when closing and discarding the new changes
   */
  onClose: () => void;
  /**
   * Completely remove Excalidraw component
   */
  onDelete: () => void;
  /**
   * Callback when the save button is clicked
   */
  onSave: (
    elements: ExcalidrawInitialElements,
    appState: Partial<AppState>,
    files: BinaryFiles
  ) => void;
};

/**
 * @explorer-desc
 * A component which renders a modal with Excalidraw (a painting app)
 * which can be used to export an editable image
 */
export default function ExcalidrawContainer({
  onSave,
  initialElements,
  initialAppState,
  initialFiles,
  onDelete,
  onClose,
}: Props): JSX.Element | null {
  const { modal } = App.useApp();
  const [excalidrawAPI, setExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  const [elements, setElements] =
    useState<ExcalidrawInitialElements>(initialElements);
  const [files, setFiles] = useState<BinaryFiles>(initialFiles);
  const [hasError, setHasError] = useState(false);

  const save = () => {
    if (elements?.some((el) => !el?.isDeleted)) {
      const appState = excalidrawAPI?.getAppState();
      const partialState: Partial<AppState> = {
        exportBackground: appState?.exportBackground,
        exportScale: appState?.exportScale,
        exportWithDarkMode: appState?.theme === 'dark',
        isBindingEnabled: appState?.isBindingEnabled,
        isLoading: appState?.isLoading,
        name: appState?.name,
        theme: appState?.theme,
        viewBackgroundColor: appState?.viewBackgroundColor,
        viewModeEnabled: appState?.viewModeEnabled,
        zenModeEnabled: appState?.zenModeEnabled,
        zoom: appState?.zoom,
      };
      // set fontFamily to 2 for all text elements
      const updated = elements?.map((el) => {
        if (el.type === 'text' && el.fontFamily !== 2) {
          return {
            ...el,
            fontFamily: 2,
          };
        }
        return el;
      });
      onSave(updated, partialState, files);
    } else {
      onDelete();
    }
  };

  const discard = () => {
    modal.confirm({
      width: 500,
      title: 'Are you sure you want to discard your changes?',
      icon: <ExclamationCircleFilled />,
      content: 'This action cannot be undone.',
      okButtonProps: {
        shape: 'round',
      },
      cancelButtonProps: {
        shape: 'round',
      },
      onOk() {
        onClose();
      },
    });
  };

  const onChange = (
    els: ExcalidrawInitialElements,
    _: AppState,
    fls: BinaryFiles
  ) => {
    try {
      setElements(els);
      setFiles(fls);
      setHasError(false);
    } catch (error) {
      console.error('Excalidraw onChange error:', error);
      setHasError(true);
    }
  };

  useEffect(() => {
    // Reset error state when component mounts or reinitializes
    setHasError(false);
  }, []);

  return (
    <div className="space-y-4">
      {hasError && (
        <Alert
          message="Drawing Error"
          description="An error occurred while drawing. Please try again or refresh the page if the issue persists."
          type="warning"
          closable
          onClose={() => setHasError(false)}
        />
      )}
      <div
        style={{ height: '80vh' }}
        className="border border-gray-500 rounded-lg overflow-hidden"
      >
        {/* hide the useless plugin and font picker */}
        <style>
          {`
            .dropdown-menu-item:has(path[d^="M407.48"]) {
              display: none !important;
            }
            fieldset:has(.FontPicker__container) {
              display: none !important;
            }
          `}
        </style>
        {!hasError && (
          <Excalidraw
            onChange={onChange}
            excalidrawAPI={setExcalidrawAPI}
            aiEnabled={false}
            initialData={{
              appState: initialAppState || { isLoading: false },
              elements: initialElements,
              files: initialFiles,
            }}
            validateBounds={false}
          />
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button shape="round" onClick={discard}>
          Discard
        </Button>
        <Button shape="round" type="primary" onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
}
